import type { CrisisResource } from '@adhd/shared';
import { Link } from 'react-router-dom';

/**
 * What the app says back when somebody writes something frightening.
 *
 * WHY THIS EXISTS. The lexical crisis check was built as a floor — hard-coded
 * patterns, hard-coded numbers, no key, no network, no model — and then wired
 * into exactly one caller: a turn of conversation with the voice companion.
 * So somebody who typed "I keep thinking about killing myself" into the journal
 * got a stored transcript and silence, and somebody who had switched AI off
 * could not have reached the check at all. A floor with one door is not a floor.
 *
 * IT NEVER BLOCKS THE SAVE. This appears beside a successful write, never
 * instead of one. Refusing to keep what somebody just managed to write down, at
 * the moment they wrote it, would be the app protecting itself rather than
 * them — and the entry is the thing a clinician would want to see anyway.
 *
 * IT DOES NOT DIAGNOSE AND DOES NOT ESCALATE. No alert, no confirm, nothing
 * that has to be dismissed before the person can carry on, and nothing sent
 * anywhere. It is the numbers, and one sentence saying why they are there.
 * The person is an adult who just told the truth in their own private journal;
 * the app's job is to make the next step short, not to take it for them.
 */
export interface CrisisNotice {
  level: 'elevated' | 'high';
  resources: CrisisResource[];
  emergencyNote: string;
}

export function CrisisNoticeCard({
  notice,
  onDismiss,
}: {
  notice: CrisisNotice;
  /** Omitted where the card sits on a screen the person is about to leave. */
  onDismiss?: () => void;
}): JSX.Element {
  return (
    <div className="banner banner-crisis stack-sm stack" role="status">
      <strong>
        {notice.level === 'high'
          ? 'That sounded like a hard thing to write down. It is saved.'
          : 'Saved. Leaving these here in case they are useful.'}
      </strong>
      <p className="crisis-emergency">{notice.emergencyNote}</p>
      {notice.resources.map((r) => (
        <div className="crisis-contact" key={r.name}>
          <strong>{r.name}</strong> — {r.contact}
          {(r.dial || r.sms) && (
            <div className="crisis-actions">
              {r.dial && (
                <a className="btn btn-crisis" href={`tel:${r.dial}`}>
                  Call
                </a>
              )}
              {r.sms && (
                <a
                  className="btn btn-crisis"
                  href={`sms:${r.sms.number}${r.sms.body ? `?&body=${encodeURIComponent(r.sms.body)}` : ''}`}
                >
                  {r.sms.body ? `Text ${r.sms.body}` : 'Text'}
                </a>
              )}
            </div>
          )}
          <span className="faint">{r.description}</span>
        </div>
      ))}
      <Link className="btn btn-secondary btn-block" to="/sos">
        Something to do right now
      </Link>
      {onDismiss && (
        <button className="btn btn-ghost btn-block" onClick={onDismiss}>
          Close
        </button>
      )}
    </div>
  );
}
